<template>
  <div class="email-create-message-wrapper">
    <!-- Subject -->
    <div>
      <p class="sub-title ion-no-margin paddingBottom">Subject</p>
      <div class="counter-wrapper">
        <VTextField
          v-model="emailSubject"
          type="text"
          :readonly="isViewMode"
          :maxlength="1000"
          placeholder="Enter Subject"
          aria-label="Email subject"
          appearance="ghost"
          class="vivid-input"
        />
        <div class="custom-counter">
          {{ (emailSubject || '').length }}/1000
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="email-field marginBottom">
      <p class="sub-title ion-no-margin paddingBottom">Body</p>

      <!-- Editor Type Toggle -->
      <div class="editor-type-toggle marginBottom">
        <label
          class="editor-option"
          :class="{ selected: editorType === 'plaintext' }"
          @click="!isViewMode && (editorType = 'plaintext')"
          for="email-editor-plaintext"
        >
          <img :src="plainTextEditorIcon" alt="Plain text editor" width="20" />
          <span>Plain Text Editor</span>
          <input
            type="radio"
            value="plaintext"
            v-model="editorType"
            :disabled="isViewMode"
            class="radio-hidden"
            id="email-editor-plaintext"
          />
          <div class="radio-indicator" :class="{ active: editorType === 'plaintext' }"></div>
        </label>
        <label
          class="editor-option"
          :class="{ selected: editorType === 'html' }"
          @click="!isViewMode && (editorType = 'html')"
          for="email-editor-html"
        >
          <VIcon name="code-line" :size="-4" />
          <span>HTML Editor</span>
          <input
            type="radio"
            value="html"
            v-model="editorType"
            :disabled="isViewMode"
            class="radio-hidden"
            id="email-editor-html"
          />
          <div class="radio-indicator" :class="{ active: editorType === 'html' }"></div>
        </label>
      </div>

      <!-- Size Info -->
      <div class="info-box marginBottom">
        <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon" />
        <p class="info-text ion-no-margin">The email body has a maximum size of 100 KB.</p>
      </div>

      <!-- Plain Text Editor -->
      <div v-if="editorType === 'plaintext'">
        <RichTextBoxV2
          v-model="emailBody"
          :readonly="isViewMode"
          :showVariableInsertion="true"
          :showEmoji="true"
          :showCharCount="false"
          :maxLength="102400"
          placeholder="Enter text"
        />
      </div>

      <!-- HTML Editor with sub-tabs -->
      <div v-else class="email-editor-container">
        <!-- Sub-tabs: Visual Editor / HTML Source -->
        <div class="html-editor-tabs">
          <button
            class="html-editor-tab"
            :class="{ active: htmlEditorMode === 'visual' }"
            @click="htmlEditorMode = 'visual'"
          >
            Visual Editor
          </button>
          <button
            class="html-editor-tab"
            :class="{ active: htmlEditorMode === 'source' }"
            @click="htmlEditorMode = 'source'"
          >
            HTML Source
          </button>
        </div>

        <!-- Visual Editor (Vivid RTE) -->
        <div v-show="htmlEditorMode === 'visual'" class="visual-editor-wrapper">
          <VRichTextEditor v-if="rteReady" :instance="_rteInstance">
            <template #text-color-picker>
              <VSimpleColorPicker :swatches="colorSwatches" />
            </template>
          </VRichTextEditor>
          <div class="visual-editor-footer">
            <div class="footer-actions">
              <div class="variable-popover-wrapper">
                <VIcon name="curly-brackets-line" class="variable-insert-btn" title="Insert variable" @click="toggleVariablePopover" />
                <div v-if="showVariablePopover" class="variable-popover">
                  <div class="variable-list">
                    <div v-for="item in builtInVariables" :key="item.key" class="variable-list__item" @click="insertVariableInRte(item.key)">
                      {{ item.label }}
                    </div>
                    <div class="variable-list__divider"></div>
                    <div class="variable-list__item variable-list__item--action" @click="openCustomFieldPicker">Custom Field…</div>
                  </div>
                </div>
              </div>
              <div class="emoji-popover-wrapper">
                <VIcon name="emoji-line" class="emoji-insert-btn" title="Insert emoji" @click="showEmojiPicker = !showEmojiPicker" />
                <div v-if="showEmojiPicker" class="emoji-popover">
                  <EmojiPicker :native="true" :disable-skin-tones="true" :display-recent="false" :hide-search="false" :disableFooter="true" :showSearch="false" :showPreview="false" :showCategories="true" @select="onEmojiSelectedRte" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- HTML Source Editor -->
        <div v-show="htmlEditorMode === 'source'" class="source-editor-wrapper">
          <label for="email-html-source" class="sr-only">HTML source code</label>
          <textarea
            id="email-html-source"
            v-model="emailBodyHtml"
            :readonly="isViewMode"
            class="email-textarea html-source"
            placeholder="Paste or type HTML content here"
            @blur="onHtmlSourceBlur"
          ></textarea>
        </div>
      </div>

      <!-- Message Size -->
      <div class="message-size-footer">
        <span class="message-size">Message Size: {{ messageSizeFormatted }}</span>
        <VIcon v-if="editorType === 'html' && htmlEditorMode === 'source'" name="copy-2-line" class="copy-icon" title="Copy to clipboard" @click="copyHtmlToClipboard" />
      </div>
    </div>

    <!-- Message Properties - shown for both Plain Text and HTML editors -->
    <div class="email-field">
      <h3 class="properties-title">Message Properties (Optional)</h3>
      <p class="properties-description">Configure message properties such as Importance, Priority, and Sensitivity to indicate how recipients should treat this email.</p>

      <div class="properties-grid">
        <div class="property-card">
          <div class="property-header">
            <div class="property-content">
              <span class="property-label">Importance</span>
              <HelpTooltip>The importance of the email message. This will be mapped to the Importance header in the email received by the end user.</HelpTooltip>
            </div>
            <VSwitch v-model="emailProperties.importance" connotation="cta" :disabled="isViewMode" />
          </div>
          <div v-if="emailProperties.importance" class="property-dropdown">
            <VSelect v-model="emailProperties.importanceLevel" :disabled="isViewMode" appearance="ghost" aria-label="Importance level" class="vivid-select">
              <VOption value="low" text="Low"></VOption>
              <VOption value="normal" text="Normal"></VOption>
              <VOption value="high" text="High"></VOption>
            </VSelect>
          </div>
        </div>

        <div class="property-card">
          <div class="property-header">
            <div class="property-content">
              <span class="property-label">Priority</span>
              <HelpTooltip>The priority of the email message. This will be mapped to the Priority header in the email received by the end user.</HelpTooltip>
            </div>
            <VSwitch v-model="emailProperties.priority" connotation="cta" :disabled="isViewMode" />
          </div>
          <div v-if="emailProperties.priority" class="property-dropdown">
            <VSelect v-model="emailProperties.priorityLevel" :disabled="isViewMode" appearance="ghost" aria-label="Priority level" class="vivid-select">
              <VOption value="non-urgent" text="Non-urgent"></VOption>
              <VOption value="normal" text="Normal"></VOption>
              <VOption value="urgent" text="Urgent"></VOption>
            </VSelect>
          </div>
        </div>

        <div class="property-card">
          <div class="property-header">
            <div class="property-content">
              <span class="property-label">Sensitivity</span>
              <HelpTooltip>The sensitivity of the email message. This will be mapped to the Sensitivity header in the email received by the end user.</HelpTooltip>
            </div>
            <VSwitch v-model="emailProperties.sensitivity" connotation="cta" :disabled="isViewMode" />
          </div>
          <div v-if="emailProperties.sensitivity" class="property-dropdown">
            <VSelect v-model="emailProperties.sensitivityLevel" :disabled="isViewMode" appearance="ghost" aria-label="Sensitivity level" class="vivid-select">
              <VOption value="personal" text="Personal"></VOption>
              <VOption value="private" text="Private"></VOption>
              <VOption value="company-confidential" text="Company-Confidential"></VOption>
            </VSelect>
          </div>
        </div>
      </div>
    </div>

    <!-- Variable Picker Modal -->
    <CustomDataFieldPickerModal v-model:isOpen="showVariablePicker" @selected="onVariableSelected" canAddNew />
  </div>
</template>

<script>
import HelpTooltip from "@/components/HelpTooltip.vue";
import RichTextBoxV2 from "@/components/rich-text-box-v2.vue";
import { VRichTextEditor } from "@vonage/vivid-vue";
import { VSimpleColorPicker } from "@vonage/vivid-vue";
import { useUIStore } from '@/stores/ui';
import CustomDataFieldPickerModal from "@/components/CustomDataFieldPickerModal.vue";
import EmojiPicker from 'vue3-emoji-picker';
import {
  RteConfig,
  RteBase,
  RteToolbarFeature,
  RteBoldFeature,
  RteItalicFeature,
  RteUnderlineFeature,
  RteStrikethroughFeature,
  RteMonospaceFeature,
  RteListFeature,
  RteAlignmentFeature,
  RteLinkFeature,
  RteHardBreakFeature,
  RteInlineImageFeature,
  RteTextBlockPickerFeature,
  RtePlaceholderFeature,
  RteHtmlSerializer,
  RteFontSizePickerFeature,
  RteTextColorPickerFeature,
  RteFileHandlerFeature,
} from "@vonage/vivid";
import plainTextEditorIcon from '@/resources/images/plane_text_editor.svg';

// Create RTE config at module level (outside Vue reactivity)
const rteConfig = new RteConfig([
  new RteBase({
    heading1: true,
    heading2: true,
    heading3: true,
  }),
  new RteToolbarFeature(),
  new RtePlaceholderFeature({ text: 'Start composing your email...' }),
  new RteFontSizePickerFeature({
    options: [
      { size: '24px', label: 'Extra Large' },
      { size: '18px', label: 'Large' },
      { size: '14px', label: 'Normal' },
      { size: '12px', label: 'Small' },
    ],
    onBlocks: [{ node: 'paragraph', defaultSize: '14px' }],
  }),
  new RteBoldFeature(),
  new RteItalicFeature(),
  new RteUnderlineFeature(),
  new RteStrikethroughFeature(),
  new RteMonospaceFeature(),
  new RteTextColorPickerFeature({
    onBlocks: [
      { node: 'heading1', defaultColor: '#000000' },
      { node: 'heading2', defaultColor: '#000000' },
      { node: 'heading3', defaultColor: '#000000' },
      { node: 'paragraph', defaultColor: '#000000' },
    ],
  }),
  new RteListFeature({ bulletList: true, numberedList: true }),
  new RteAlignmentFeature(),
  new RteLinkFeature(),
  new RteHardBreakFeature(),
  new RteInlineImageFeature(),
  new RteFileHandlerFeature({
    handleFiles: async (files) => {
      // For now, convert dropped/pasted images to inline data URLs
      const fragments = [];
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          const dataUrl = await fileToDataUrl(file);
          fragments.push({
            type: 'inlineImage',
            attrs: { imageUrl: dataUrl, alt: file.name, size: null, naturalWidth: 0, naturalHeight: 0 },
          });
        }
      }
      return fragments.length ? fragments : null;
    },
  }),
  new RteTextBlockPickerFeature({
    options: [
      { node: 'heading1', label: 'Heading 1' },
      { node: 'heading2', label: 'Heading 2' },
      { node: 'heading3', label: 'Heading 3' },
      { node: 'paragraph', label: 'Paragraph' },
    ],
  }),
]);

function fileToDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

const rteHtmlSerializer = new RteHtmlSerializer(rteConfig);

export default {
  name: 'EmailCreateMessage',
  components: {
    HelpTooltip,
    RichTextBoxV2,
    VRichTextEditor,
    VSimpleColorPicker,
    CustomDataFieldPickerModal,
    EmojiPicker,
  },
  props: {
    editBroadcast: {
      type: Object,
      default: () => ({})
    },
    isViewMode: {
      type: Boolean,
      default: false
    },
  },
  emits: ['emailDataUpdate'],
  data() {
    return {
      plainTextEditorIcon,
      emailSubject: '',
      emailBody: '',
      emailBodyHtml: '',
      emailBodyVisual: '',
      editorType: 'plaintext',
      htmlEditorMode: 'visual',
      rteReady: false,
      showVariablePopover: false,
      showVariablePicker: false,
      showEmojiPicker: false,
      colorSwatches: [
        { label: 'Black', value: '#000000' },
        { label: 'Red', value: '#E61D1D' },
        { label: 'Yellow', value: '#FA9F00' },
        { label: 'Green', value: '#1C8731' },
        { label: 'Blue', value: '#0276D5' },
        { label: 'Purple', value: '#9941FF' },
        { label: 'Pink', value: '#D6219C' },
      ],
      builtInVariables: [
        { key: '<userfirstname>', label: "User's First Name" },
        { key: '<userlastname>', label: "User's Last Name" },
        { key: '<userfirstname> <userlastname>', label: "User's Full Name" },
        { key: '<userphone>', label: "User's Mobile Number" },
        { key: '<useremail>', label: "User's Email ID" },
      ],
      emailProperties: {
        importance: false,
        importanceLevel: 'normal',
        priority: false,
        priorityLevel: 'normal',
        sensitivity: false,
        sensitivityLevel: 'private',
      },
    };
  },
  computed: {
    currentBody() {
      if (this.editorType === 'plaintext') {
        return this.emailBody;
      }
      // HTML editor: return content based on active sub-mode
      if (this.htmlEditorMode === 'visual') {
        return this.emailBodyVisual;
      }
      return this.emailBodyHtml;
    },
    messageSizeBytes() {
      const content = this.currentBody || '';
      return new TextEncoder().encode(content).length;
    },
    messageSizeFormatted() {
      const bytes = this.messageSizeBytes;
      if (bytes < 1024) {
        return `${bytes} B`;
      }
      return `${(bytes / 1024).toFixed(1)} KB`;
    },
    isOverSizeLimit() {
      return this.messageSizeBytes > 100 * 1024;
    },
  },
  watch: {
    emailSubject() {
      this.emitUpdate();
    },
    emailBody() {
      this.emitUpdate();
    },
    emailBodyHtml() {
      this.emitUpdate();
    },
    emailBodyVisual() {
      this.emitUpdate();
    },
    editorType() {
      this.emitUpdate();
    },
    htmlEditorMode(newMode) {
      // Each mode is independent - no syncing between visual and source
      this.emitUpdate();
    },
    emailProperties: {
      handler() {
        this.emitUpdate();
      },
      deep: true,
    },
    // Watch only the email_data payload (not the whole editBroadcast). Deep-watching
    // editBroadcast would re-run populateFromBroadcast on any unrelated parent mutation
    // (e.g. distribution_probability when A/B is toggled), wiping in-progress edits.
    'editBroadcast.email_data': {
      handler(newVal) {
        if (newVal) {
          this.populateFromBroadcast(newVal);
        }
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    // Create the RTE instance outside of reactive data to avoid Proxy wrapping.
    // Vue Proxy breaks Symbol-based property access that the web component needs.
    this._rteInstance = rteConfig.instantiateEditor({
      onChange: () => {
        this.syncVisualToHtmlSource();
        this.emitUpdate();
      },
    });
  },
  mounted() {
    this.rteReady = true;
  },
  methods: {
    syncVisualToHtmlSource() {
      if (!this._rteInstance) return;
      try {
        const doc = this._rteInstance.getDocument();
        this.emailBodyVisual = rteHtmlSerializer.serializeDocument(doc);
      } catch (e) {
        // Serialization error - leave emailBodyVisual unchanged
      }
    },
    onHtmlSourceBlur() {
      // Just emit update when user leaves the HTML source textarea
      this.emitUpdate();
    },
    copyHtmlToClipboard() {
      if (this.emailBodyHtml) {
        navigator.clipboard.writeText(this.emailBodyHtml);
        useUIStore().updateToast({ open: true, message: 'HTML copied to clipboard', type: 'success' });
      }
    },
    onVariableSelected(variable) {
      this.showVariablePicker = false;
      if (!this._rteInstance || !variable) return;
      this._rteInstance.replaceSelection([{ type: 'text', text: variable }]);
    },
    toggleVariablePopover() {
      this.showVariablePopover = !this.showVariablePopover;
    },
    insertVariableInRte(variable) {
      this.showVariablePopover = false;
      if (!this._rteInstance || !variable) return;
      this._rteInstance.replaceSelection([{ type: 'text', text: variable }]);
    },
    openCustomFieldPicker() {
      this.showVariablePopover = false;
      this.showVariablePicker = true;
    },
    onEmojiSelectedRte(emoji) {
      this.showEmojiPicker = false;
      if (!this._rteInstance || !emoji?.i) return;
      this._rteInstance.replaceSelection([{ type: 'text', text: emoji.i }]);
    },
    calculateMessageSize() {
      // Reactivity handles this via computed property
    },
    populateFromBroadcast(emailData) {
      if (!emailData) return;
      this.emailSubject = emailData.subject || '';
      this.editorType = emailData.editor_type || 'plaintext';
      this.htmlEditorMode = emailData.html_editor_mode || 'visual';
      if (this.editorType === 'plaintext') {
        this.emailBody = emailData.body || '';
      } else if (this.htmlEditorMode === 'source') {
        this.emailBodyHtml = emailData.body || '';
      } else {
        this.emailBodyVisual = emailData.body || '';
      }
      if (emailData.properties) {
        this.emailProperties = { ...this.emailProperties, ...emailData.properties };
      }
    },
    emitUpdate() {
      this.$emit('emailDataUpdate', {
        subject: this.emailSubject,
        body: this.currentBody,
        editor_type: this.editorType,
        html_editor_mode: this.htmlEditorMode,
        properties: { ...this.emailProperties },
      });
    },
    getEmailData() {
      // Ensure visual editor content is serialized before returning
      if (this.editorType === 'html' && this.htmlEditorMode === 'visual') {
        this.syncVisualToHtmlSource();
      }
      return {
        subject: this.emailSubject,
        body: this.currentBody,
        editor_type: this.editorType,
        html_editor_mode: this.htmlEditorMode,
        properties: { ...this.emailProperties },
      };
    },
    validate() {
      if (!this.emailSubject?.trim()) {
        return { valid: false, message: 'Email subject is required.' };
      }
      if (!this.currentBody?.trim()) {
        return { valid: false, message: 'Email body is required.' };
      }
      if (this.isOverSizeLimit) {
        return { valid: false, message: 'Email body exceeds the maximum size of 100 KB.' };
      }
      return { valid: true };
    },
  },
};
</script>

<style scoped lang="scss">
.email-create-message-wrapper {
  margin-top: 20px;

  .email-field {
    margin-bottom: 16px;
  }

  .editor-type-toggle {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    .editor-option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      border: 1px solid var(--vvd-color-neutral-200, #e0e0e0);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;

      &.selected {
        border-color: var(--vvd-color-cta-500, #6C63FF);
        background: var(--vvd-color-cta-50, #f3f2ff);
      }

      &:hover:not(.selected) {
        border-color: var(--vvd-color-neutral-400, #bdbdbd);
      }

      .radio-hidden {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      .radio-indicator {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid var(--vvd-color-neutral-300, #ccc);
        margin-left: auto;
        position: relative;

        &.active {
          border-color: var(--vvd-color-cta-500, #6C63FF);

          &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: var(--vvd-color-cta-500, #6C63FF);
          }
        }
      }

      span {
        font-size: 14px;
        font-weight: 500;
      }
    }
  }

  .email-editor-container {
    border-radius: 5px;
    border: 1px solid var(--background-primary);
    overflow: hidden;
  }

  .html-editor-tabs {
    display: flex;
    border-bottom: 1px solid var(--background-primary);

    .html-editor-tab {
      flex: 1;
      padding: 12px 16px;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      color: var(--vvd-color-neutral-600, #757575);
      transition: all 0.2s ease;

      &.active {
        color: var(--vvd-color-cta-500, #6C63FF);
        border-bottom-color: var(--vvd-color-cta-500, #6C63FF);
      }

      &:hover:not(.active) {
        color: var(--vvd-color-neutral-800, #424242);
      }
    }
  }

  .visual-editor-wrapper {

    ::v-deep(vwc-rich-text-editor) {
      --rich-text-editor-padding-inline: 16px;
      --rich-text-editor-padding-block: 12px;
    }

    ::v-deep(::part(editor)) {
      min-block-size: 300px;
    }

    .visual-editor-footer {
      display: flex;
      justify-content: flex-end;
      padding: 8px 16px;
      border-top: 1px solid var(--vvd-color-neutral-200, #e0e0e0);

      .footer-actions {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .variable-popover-wrapper,
      .emoji-popover-wrapper {
        position: relative;
      }

      .variable-insert-btn,
      .emoji-insert-btn {
        cursor: pointer;
        color: var(--vvd-color-neutral-600, #616161);

        &:hover {
          color: var(--vvd-color-cta-500, #6C63FF);
        }
      }

      .variable-popover,
      .emoji-popover {
        position: absolute;
        bottom: 100%;
        right: 0;
        margin-bottom: 8px;
        background: #fff;
        border: 1px solid var(--vvd-color-neutral-200, #e0e0e0);
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 100;
      }

      .variable-popover {
        min-width: 200px;

        .variable-list {
          padding: 8px 0;

          &__item {
            padding: 8px 16px;
            cursor: pointer;
            font-size: 13px;
            white-space: nowrap;

            &:hover {
              background: var(--vvd-color-neutral-50, #fafafa);
            }

            &--action {
              color: var(--vvd-color-cta-500, #6C63FF);
              font-weight: 500;
            }
          }

          &__divider {
            height: 1px;
            background: var(--vvd-color-neutral-200, #e0e0e0);
            margin: 4px 0;
          }
        }
      }
    }
  }

  .source-editor-wrapper {
    .email-textarea.html-source {
      width: 100%;
      min-height: 250px;
      padding: 16px;
      border: none;
      outline: none;
      resize: vertical;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      line-height: 1.6;
      background: var(--background-primary);

      &::placeholder {
        color: var(--vvd-color-neutral-400, #9e9e9e);
      }

      &:focus {
        background: var(--white);
      }
    }
  }

  .message-size-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;

    .message-size {
      font-size: 12px;
      color: var(--gray-04);
    }

    .copy-icon,
    .variable-icon {
      cursor: pointer;
      color: var(--vvd-color-neutral-600, #616161);

      &:hover {
        color: var(--vvd-color-cta-500, #6C63FF);
      }
    }
  }

  .properties-title {
    font-size: 18px;
    font-weight: 400;
    margin: 0 0 8px;
  }

  .properties-description {
    font-size: 14px;
    color: var(--gray-04);
    font-weight: 400;
    margin: 0 0 16px;
  }

  .properties-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .property-card {
      padding: 16px;
      border: 1px solid var(--background-primary);
      border-radius: 5px;
      background: var(--white);

      .property-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .property-content {
        display: flex;
        align-items: center;
        gap: 4px;

        .property-label {
          font-size: 14px;
          font-weight: 500;
        }
      }

      .property-dropdown {
        margin-top: 12px;
      }
    }
  }
}

.info-box {
  display: flex;
  align-items: center;
  flex-direction: row;
  background: var(--purple-opac);
  padding: 15px 10px;
  border-radius: 5px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.info-text {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
  margin-left: 8px;
}
</style>
